import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lightbulb, Send, Plus, Star, Users, Calendar, TrendingUp,
  CheckCircle, Clock, Layers, ChevronRight, ArrowRight, Sparkles,
  BookOpen, X, MessageSquare, Target, Award, BarChart3, Trophy, Inbox,
  AlertCircle, RefreshCw, GraduationCap, ExternalLink, CircleDot, Loader2
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, LineChart, Line,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Progress } from '@/components/ui/progress';
import { useApp } from '@/context/AppContext';
import { useSTNAuth } from '@/context/STNAuthContext';
import { toast } from 'sonner';
import { cn, programTag } from '@/lib/utils';
import {
  createProgramIdea,
  listProgramIdeas,
  getPartnerBatches,
  getPartnerBatchAnalytics,
  getPartnerChangeRequests,
  reviewPartnerChangeRequest,
} from '@/api/stnApi';

// ────────────────────────────────────────────────────────────────
// PROGRAMS SECTION — startups see only programs matching portfolio
// ────────────────────────────────────────────────────────────────
export const ProgramsSection = ({ highlightId }) => {
  const { programs } = useApp();
  const { startup } = useSTNAuth();
  const [openDetail, setOpenDetail] = useState(null);
  const refs = useRef({});

  // Real startup identity (was hardcoded "TechVision AI" / mock portfolio).
  const companyName   = startup?.companyName || '';
  const portfolioTags = startup?.portfolio || startup?.goals || [];

  // Filter: programs assigned to this company OR portfolio tags overlap
  const myPrograms = useMemo(() => programs.filter(p =>
    (companyName && p.assignedCompanies.includes(companyName)) ||
    p.portfolioTags.some(t => portfolioTags.includes(t))
  ), [programs, companyName, portfolioTags]);

  useEffect(() => {
    if (highlightId && refs.current[highlightId]) {
      refs.current[highlightId].scrollIntoView({ behavior: 'smooth', block: 'center' });
      const program = programs.find(p => p.id === highlightId);
      if (program) setTimeout(() => setOpenDetail(program), 400);
    }
  }, [highlightId, programs]);

  return (
    <div className="space-y-5" data-testid="programs-section">
      {/* Info banner */}
      <div className="p-4 rounded-xl bg-info/8 border border-info/20 flex items-start gap-3">
        <Sparkles className="w-4 h-4 text-info flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-foreground">Programs matched to your portfolio</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            We surface mentor-hosted programs aligned with {companyName || 'your startup'}'s portfolio tags: {portfolioTags.slice(0, 3).join(' • ')}.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {myPrograms.map(p => {
          const isHighlight = p.id === highlightId;
          return (
            <Card
              key={p.id}
              ref={el => (refs.current[p.id] = el)}
              data-testid={`program-card-${p.id}`}
              className={cn(
                'border-border hover:shadow-card transition-all',
                isHighlight && 'ring-2 ring-primary/40 shadow-card'
              )}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3 gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium',
                        p.status === 'active' ? 'badge-success' : 'badge-muted'
                      )}>{p.status}</span>
                      <span className="text-xs text-muted-foreground">{p.duration} • {p.sessionsPerBatch} sessions</span>
                      {programTag(p.programRef, p.program_ref, p.programId, p.program_id, p.id) && (
                        <span title="Program ID" className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted/60 text-muted-foreground border border-border">
                          {programTag(p.programRef, p.program_ref, p.programId, p.program_id, p.id)}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-foreground text-base leading-snug">{p.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5 mb-3">
                  {p.portfolioTags.slice(0, 3).map(t => <span key={t} className="badge-primary text-xs">{t}</span>)}
                </div>

                <div className="grid grid-cols-4 gap-2 mb-4 p-3 rounded-lg bg-muted/40">
                  <div>
                    <p className="text-xs text-muted-foreground">Batches</p>
                    <p className="font-bold text-foreground text-sm">{p.totalBatches}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Enrolled</p>
                    <p className="font-bold text-foreground text-sm">{p.totalEnrolled}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Rating</p>
                    <p className="font-bold text-foreground text-sm flex items-center gap-0.5">
                      {p.avgRating > 0 ? (<><Star className="w-3 h-3 fill-warning text-warning" /> {p.avgRating}</>) : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Per seat</p>
                    <p className="font-bold text-foreground text-sm">₹{(p.ratePerSeat / 1000).toFixed(0)}k</p>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full gradient-primary flex items-center justify-center text-xs font-bold text-primary-foreground">
                      {p.mentorName.split(' ').slice(0, 2).map(n => n[0]).join('')}
                    </div>
                    <span className="text-xs text-muted-foreground">{p.mentorName}</span>
                  </div>
                  <Button size="sm" variant="outline" className="border-border gap-1" onClick={() => setOpenDetail(p)} data-testid={`program-detail-btn-${p.id}`}>
                    Details <ChevronRight className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {myPrograms.length === 0 && (
        <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl">
          <BookOpen className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
          <p className="text-muted-foreground">No programs matched to your portfolio yet.</p>
        </div>
      )}

      <ProgramDetailSheet program={openDetail} onClose={() => setOpenDetail(null)} />
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// PROGRAM DETAIL SHEET (also visible to StartupNexus admin view)
// ────────────────────────────────────────────────────────────────
const ProgramDetailSheet = ({ program, onClose }) => {
  const { batches } = useApp();
  if (!program) return null;
  const programBatches = batches.filter(b => b.programId === program.id);

  return (
    <Sheet open={!!program} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto" data-testid="program-detail-sheet">
        <SheetHeader>
          <SheetTitle className="text-lg">{program.title}</SheetTitle>
        </SheetHeader>

        <div className="space-y-5 mt-5">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
            <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center text-xs font-bold text-primary-foreground">
              {program.mentorName.split(' ').slice(0, 2).map(n => n[0]).join('')}
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">{program.mentorName}</p>
              <p className="text-xs text-muted-foreground">Program Host • Created {program.createdAt}</p>
            </div>
            <span className={cn('text-xs px-2 py-0.5 rounded-full', program.status === 'active' ? 'badge-success' : 'badge-muted')}>
              {program.status}
            </span>
          </div>

          <p className="text-sm text-muted-foreground leading-relaxed">{program.description}</p>

          <div className="grid grid-cols-4 gap-3">
            {[
              { l: 'Total Batches', v: program.totalBatches, icon: Layers, c: 'text-primary' },
              { l: 'Enrolled', v: program.totalEnrolled, icon: Users, c: 'text-chart-2' },
              { l: 'Avg Rating', v: program.avgRating > 0 ? program.avgRating : '—', icon: Star, c: 'text-warning' },
              { l: 'Revenue', v: `₹${(program.totalRevenue / 1000).toFixed(0)}k`, icon: TrendingUp, c: 'text-success' },
            ].map(s => (
              <div key={s.l} className="p-3 rounded-lg border border-border">
                <s.icon className={cn('w-4 h-4 mb-1.5', s.c)} />
                <p className="font-bold text-foreground text-sm">{s.v}</p>
                <p className="text-xs text-muted-foreground">{s.l}</p>
              </div>
            ))}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <Target className="w-4 h-4 text-primary" /> Portfolio Tags
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {program.portfolioTags.map(t => <span key={t} className="badge-primary text-xs">{t}</span>)}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <Users className="w-4 h-4 text-chart-2" /> Assigned Companies ({program.assignedCompanies.length})
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {program.assignedCompanies.length > 0
                ? program.assignedCompanies.map(c => <span key={c} className="badge-muted">{c}</span>)
                : <span className="text-xs text-muted-foreground">No companies assigned yet.</span>}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <Layers className="w-4 h-4 text-chart-3" /> Batches Run Under This Program
            </h4>
            {programBatches.length === 0 ? (
              <p className="text-xs text-muted-foreground py-3">No batches yet.</p>
            ) : (
              <div className="space-y-2">
                {programBatches.map(b => (
                  <div key={b.id} className="p-3 rounded-lg border border-border">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-foreground">{b.name}</p>
                      <span className={cn('text-xs px-2 py-0.5 rounded-full',
                        b.status === 'ongoing' ? 'badge-info' :
                        b.status === 'completed' ? 'badge-success' : 'badge-warning'
                      )}>{b.status}</span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                      <span>{b.startDate} → {b.endDate}</span>
                      <span>{b.enrolled}/{b.capacity} seats</span>
                      <span className="flex items-center gap-0.5"><Star className="w-3 h-3 fill-warning text-warning" />{b.avgRating}</span>
                      <span>NPS {b.nps}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ────────────────────────────────────────────────────────────────
// BATCHES SECTION — analytics per batch, organized by program
// ────────────────────────────────────────────────────────────────
export const BatchesSection = ({ highlightId }) => {
  const { batches, programs } = useApp();
  const [filter, setFilter] = useState('all');
  const [openBatch, setOpenBatch] = useState(null);
  const refs = useRef({});

  useEffect(() => {
    if (highlightId && refs.current[highlightId]) {
      refs.current[highlightId].scrollIntoView({ behavior: 'smooth', block: 'center' });
      const batch = batches.find(b => b.id === highlightId);
      if (batch) setTimeout(() => setOpenBatch(batch), 400);
    }
  }, [highlightId, batches]);

  const filteredBatches = filter === 'all' ? batches : batches.filter(b => b.status === filter);

  // Group by program
  const grouped = programs.map(p => ({
    program: p,
    batches: filteredBatches.filter(b => b.programId === p.id),
  })).filter(g => g.batches.length > 0);

  return (
    <div className="space-y-5" data-testid="batches-section">
      <div className="flex items-center gap-2 flex-wrap">
        {['all', 'ongoing', 'completed', 'upcoming'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`batch-filter-${f}`}
            className={cn(
              'text-xs px-3 py-1.5 rounded-full font-medium transition-colors capitalize border',
              filter === f
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border bg-muted/40 text-muted-foreground hover:text-foreground'
            )}
          >
            {f} ({f === 'all' ? batches.length : batches.filter(b => b.status === f).length})
          </button>
        ))}
      </div>

      {grouped.map(g => (
        <div key={g.program.id} className="space-y-3">
          <div className="flex items-center justify-between border-b border-border pb-2">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Program</p>
              <h3 className="text-sm font-semibold text-foreground">{g.program.title}</h3>
            </div>
            <span className="text-xs text-muted-foreground">by {g.program.mentorName}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {g.batches.map(b => {
              const isHighlight = b.id === highlightId;
              return (
                <Card
                  key={b.id}
                  ref={el => (refs.current[b.id] = el)}
                  data-testid={`batch-card-${b.id}`}
                  className={cn('border-border hover:shadow-card transition-all cursor-pointer',
                    isHighlight && 'ring-2 ring-primary/40 shadow-card'
                  )}
                  onClick={() => setOpenBatch(b)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-semibold text-sm text-foreground">{b.name}</p>
                      <span className={cn('text-xs px-2 py-0.5 rounded-full',
                        b.status === 'ongoing' ? 'badge-info' :
                        b.status === 'completed' ? 'badge-success' : 'badge-warning'
                      )}>{b.status}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3">{b.startDate} → {b.endDate}</p>

                    <div className="grid grid-cols-3 gap-2 text-center mb-3">
                      <div className="p-2 rounded-lg bg-muted/40">
                        <p className="font-bold text-foreground text-sm">{b.enrolled}/{b.capacity}</p>
                        <p className="text-xs text-muted-foreground">Seats</p>
                      </div>
                      <div className="p-2 rounded-lg bg-muted/40">
                        <p className="font-bold text-foreground text-sm flex items-center justify-center gap-0.5">
                          <Star className="w-3 h-3 fill-warning text-warning" />{b.avgRating}
                        </p>
                        <p className="text-xs text-muted-foreground">Avg rating</p>
                      </div>
                      <div className="p-2 rounded-lg bg-muted/40">
                        <p className="font-bold text-foreground text-sm">{b.nps}</p>
                        <p className="text-xs text-muted-foreground">NPS</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{b.sessionsCompleted}/{b.totalSessions} sessions</span>
                      <Progress value={(b.sessionsCompleted / b.totalSessions) * 100} className="h-1 flex-1 mx-3" />
                      <span className="text-primary font-medium">View →</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      {grouped.length === 0 && (
        <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl">
          <Layers className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
          <p className="text-muted-foreground">No batches found for this filter.</p>
        </div>
      )}

      <BatchDetailSheet batch={openBatch} onClose={() => setOpenBatch(null)} />
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// ASSIGNED BATCHES SECTION — batches NexusAI (admin) assigned to this
// startup as a partner. These are independent of the startup's own
// mentors; the startup just views their registration analytics.
// ────────────────────────────────────────────────────────────────
export const AssignedBatchesSection = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [linked, setLinked]   = useState(true);
  const [loading, setLoading] = useState(true);
  const [openNexus, setOpenNexus] = useState(null);
  const [search, setSearch] = useState('');
  const [changeReqs, setChangeReqs] = useState([]);
  const [busyReq, setBusyReq] = useState(null);
  const [rejecting, setRejecting] = useState(null); // "enrollmentId:reqId" being rejected
  const [rejectNote, setRejectNote] = useState('');

  const load = () => {
    setLoading(true);
    getPartnerBatches()
      .then((res) => { setLinked(res?.linked !== false); setBatches(res?.batches || []); })
      .catch((e) => toast.error(e?.message || 'Could not load assigned batches'))
      .finally(() => setLoading(false));
    getPartnerChangeRequests().then((r) => setChangeReqs(r?.requests || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const reviewReq = async (req, action, note) => {
    if (action === 'reject' && !note) { setRejecting(`${req.enrollmentId}:${req.reqId}`); return; }
    setBusyReq(`${req.enrollmentId}:${req.reqId}`);
    try {
      const r = await reviewPartnerChangeRequest(req.enrollmentId, req.reqId, action, note);
      toast.success(action === 'approve' ? 'Change accepted & applied' : 'Change rejected');
      setRejecting(null); setRejectNote('');
      // Update status in place (keep it in the log) instead of removing.
      setChangeReqs((p) => p.map((x) => (x.enrollmentId === req.enrollmentId && x.reqId === req.reqId)
        ? { ...x, status: r?.status || (action === 'approve' ? 'accepted' : 'rejected'), reviewNote: note || '' } : x));
    } catch (e) { toast.error(e?.message || 'Failed'); }
    finally { setBusyReq(null); }
  };

  const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
  const shown = batches.filter((b) =>
    !search || b.title?.toLowerCase().includes(search.toLowerCase()) || b.programs?.some((p) => p.toLowerCase().includes(search.toLowerCase())));

  return (
    <div className="space-y-4" data-testid="assigned-batches-section">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-semibold text-foreground">Assigned by NexusAI</h3>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-medium">Partner</span>
        <div className="relative ml-auto max-w-xs w-full">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search batch / program…" className="h-9" />
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={cn('w-4 h-4 mr-1.5', loading && 'animate-spin')} /> Refresh
        </Button>
      </div>
      <p className="text-xs text-muted-foreground -mt-1">
        Batches assigned to your organization by NexusAI. Open any batch to see its full registration & engagement analytics.
      </p>

      {/* Application change requests — open ones to review + full log */}
      {changeReqs.length > 0 && (() => {
        const openCount = changeReqs.filter((r) => ['applied', 'under_review'].includes(r.status)).length;
        return (
          <Card className="border-amber-500/30 bg-amber-500/[0.03]">
            <CardContent className="p-4">
              <p className="text-sm font-semibold flex items-center gap-2 mb-3">
                <AlertCircle className="w-4 h-4 text-amber-500" /> Application change requests
                {openCount > 0 && <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/25 text-[10px]">{openCount} to review</Badge>}
                <span className="text-[10px] text-muted-foreground ml-auto">Applied → Under Review → Accepted / Rejected</span>
              </p>
              <div className="space-y-2.5">
                {changeReqs.map((r) => {
                  const key = `${r.enrollmentId}:${r.reqId}`;
                  const busy = busyReq === key;
                  const open = ['applied', 'under_review'].includes(r.status);
                  const isRejecting = rejecting === key;
                  return (
                    <div key={key} className="rounded-lg border border-border bg-card p-3">
                      <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                        <div>
                          <p className="text-sm font-semibold text-foreground">{r.studentName || 'Intern'} <span className="text-[11px] font-normal text-muted-foreground">{r.studentEmail}</span></p>
                          <p className="text-[10px] text-muted-foreground">{r.programName} · requested {new Date(r.requestedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                        </div>
                        {open ? (
                          <div className="flex items-center gap-1.5">
                            <Button size="sm" variant="outline" className="h-7 text-xs text-rose-500 border-rose-500/30" disabled={busy} onClick={() => reviewReq(r, 'reject')}>Reject</Button>
                            <Button size="sm" className="h-7 text-xs bg-emerald-500 hover:bg-emerald-600 text-white" disabled={busy} onClick={() => reviewReq(r, 'approve')}>
                              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Accept'}
                            </Button>
                          </div>
                        ) : (
                          <Badge variant="outline" className={cn('text-[10px] capitalize',
                            r.status === 'accepted' ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/25' : 'bg-rose-500/10 text-rose-600 border-rose-500/25')}>
                            {r.status}
                          </Badge>
                        )}
                      </div>
                      <div className="space-y-1">
                        {(r.changes || []).map((c, j) => (
                          <p key={j} className="text-xs flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium">{c.label || c.field}:</span>
                            <span className="text-muted-foreground line-through">{String(c.old || '—')}</span>
                            <ArrowRight className="w-3 h-3 text-muted-foreground" />
                            <span className="text-foreground">{String(c.new || '—')}</span>
                          </p>
                        ))}
                      </div>
                      {r.reason && <p className="text-[11px] text-muted-foreground mt-1.5">Reason: {r.reason}</p>}
                      {open && <Badge variant="outline" className="text-[9px] capitalize mt-1.5">{r.status === 'under_review' ? 'Under review' : 'Applied'}</Badge>}
                      {r.status === 'rejected' && r.reviewNote && <p className="text-[11px] text-rose-600 mt-1.5">Rejected: {r.reviewNote}</p>}
                      {/* Reject-with-reason inline */}
                      {isRejecting && (
                        <div className="mt-2 flex items-center gap-2">
                          <Input value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="Reason for rejection (required)" className="h-8 text-xs" />
                          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setRejecting(null); setRejectNote(''); }}>Cancel</Button>
                          <Button size="sm" className="h-8 text-xs bg-rose-500 hover:bg-rose-600 text-white" disabled={!rejectNote.trim() || busy} onClick={() => reviewReq(r, 'reject', rejectNote.trim())}>
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Confirm reject'}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground"><RefreshCw className="w-5 h-5 animate-spin" /> Loading assigned batches…</div>
      ) : !linked ? (
        <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl">
          <Sparkles className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
          <p className="font-medium text-foreground">No assigned batches yet</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">When NexusAI attaches your organization as a partner to a batch, it appears here. Ensure your login email matches the one shared with the NexusAI team.</p>
        </div>
      ) : shown.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl">
          <Inbox className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
          <p className="text-muted-foreground">{batches.length === 0 ? 'No batches assigned to you yet.' : 'No batches match your search.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {shown.map((b) => (
            <Card key={b.id} className="border-primary/20 bg-primary/[0.03] hover:shadow-card transition-all cursor-pointer" onClick={() => navigate(`/assigned-batches/${b.id}`)}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-1.5 gap-2">
                  <p className="font-semibold text-sm text-foreground truncate">{b.title}</p>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border capitalize shrink-0">{b.status}</span>
                </div>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground border border-border">
                    {b.platform === 'InternNexus' ? 'InternNexus · TCI' : 'SkillNexus'}
                  </span>
                  {b.code && <span className="text-[11px] text-muted-foreground font-mono">{b.code}</span>}
                </div>
                <p className="text-xs text-muted-foreground truncate mb-3">{b.programs?.join(', ') || 'No program linked'}</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {b.seats} seats</span>
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtD(b.starts)}</span>
                  <span className="flex items-center gap-3 ml-auto">
                    <button onClick={(e) => { e.stopPropagation(); setOpenNexus(b); }} className="text-muted-foreground hover:text-foreground font-medium">Quick peek</button>
                    <span className="flex items-center gap-1 text-primary font-medium"><BarChart3 className="w-3.5 h-3.5" /> Open report →</span>
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <NexusBatchAnalyticsSheet batch={openNexus} onClose={() => setOpenNexus(null)} />
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// NEXUSAI BATCH ANALYTICS SHEET — registration + engagement for a
// batch the startup is partnered on (shared from admin).
// ────────────────────────────────────────────────────────────────
const NexusBatchAnalyticsSheet = ({ batch, onClose }) => {
  const [data, setData]   = useState(null);
  const [loading, setLoad] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!batch) { setData(null); setSearch(''); return; }
    setLoad(true);
    getPartnerBatchAnalytics(batch.id)
      .then(setData)
      .catch((e) => toast.error(e?.message || 'Could not load analytics'))
      .finally(() => setLoad(false));
  }, [batch]);

  const scoreCls = (s) => s >= 75 ? 'text-emerald-500' : s >= 50 ? 'text-amber-500' : 'text-rose-500';
  const s = data?.summary || {};
  const students = (data?.students || []).filter((r) =>
    !search || r.name?.toLowerCase().includes(search.toLowerCase()) || r.college?.toLowerCase().includes(search.toLowerCase()));

  const KPIS = [
    { label: 'Registered', value: s.totalRegistered },
    { label: 'Activated', value: s.activated },
    { label: 'Reset pwd', value: s.passwordReset },
    { label: 'Enrolled', value: s.enrolled },
    { label: 'Used coupon', value: s.couponConsumers },
    { label: '₹ saved', value: `₹${(s.totalCouponSaved || 0).toLocaleString('en-IN')}` },
  ];

  return (
    <Sheet open={!!batch} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-primary" /> {data?.batchTitle || batch?.title}</SheetTitle>
        </SheetHeader>
        <p className="text-xs text-muted-foreground mb-4">Assigned by NexusAI · Registration & engagement{data?.coupon ? ` · Coupon ${data.coupon.code}` : ''}</p>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground"><RefreshCw className="w-5 h-5 animate-spin" /> Loading analytics…</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {KPIS.map((k) => (
                <div key={k.label} className="p-2.5 rounded-lg bg-muted/40 text-center">
                  <p className="font-bold text-foreground text-sm leading-none">{k.value ?? 0}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{k.label}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
              <span className="flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Avg assignment: <b className={scoreCls(s.avgAssignmentScore)}>{s.avgAssignmentScore}%</b></span>
              <span>· Avg attendance: <b className="text-foreground">{s.avgAttendance}%</b></span>
            </div>

            {data?.studentsHidden ? (
              <div className="text-center py-10 text-muted-foreground text-sm border-2 border-dashed border-border rounded-xl">
                <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
                The student roster is not shared with your account.
              </div>
            ) : (
              <>
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / college" className="h-9 mb-3" />
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-xs min-w-[620px]">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                        <th className="px-2 py-2">Student</th>
                        <th className="px-2 py-2 text-center">Activated</th>
                        <th className="px-2 py-2 text-center">Coupon</th>
                        <th className="px-2 py-2 text-right">INXP</th>
                        <th className="px-2 py-2 text-center">Attend</th>
                        <th className="px-2 py-2 text-center">Assign AI</th>
                        <th className="px-2 py-2 text-center">Quiz</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((r) => (
                        <tr key={r.studentId} className="border-b border-border/60">
                          <td className="px-2 py-2">
                            <p className="font-semibold text-foreground">{r.name}</p>
                            <p className="text-[10px] text-muted-foreground">{r.college || r.email}</p>
                          </td>
                          <td className="px-2 py-2 text-center">{r.activated ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 inline" /> : <CircleDot className="w-3.5 h-3.5 text-muted-foreground/40 inline" />}</td>
                          <td className="px-2 py-2 text-center">{r.couponUses > 0 ? <span className="text-[10px] font-semibold text-amber-500">{r.couponUses}× · ₹{r.couponSaved}</span> : <span className="text-muted-foreground/40">—</span>}</td>
                          <td className="px-2 py-2 text-right font-medium">{(r.inxp || 0).toLocaleString()}</td>
                          <td className="px-2 py-2 text-center">{r.attendancePct}%</td>
                          <td className={cn('px-2 py-2 text-center font-semibold', scoreCls(r.avgAssignmentScore))}>{r.assignmentsSubmitted ? `${r.avgAssignmentScore}%` : '—'}</td>
                          <td className="px-2 py-2 text-center">{r.quizzesTaken ? `${r.quizzesPassed}/${r.quizzesTaken}` : '—'}</td>
                        </tr>
                      ))}
                      {students.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No registered students.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ────────────────────────────────────────────────────────────────
// BATCH DETAIL SHEET — full per-batch analytics + participant feedback
// ────────────────────────────────────────────────────────────────
const BatchDetailSheet = ({ batch, onClose }) => {
  if (!batch) return null;
  return (
    <Sheet open={!!batch} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto" data-testid="batch-detail-sheet">
        <SheetHeader>
          <SheetTitle className="text-lg">{batch.name}</SheetTitle>
          <p className="text-xs text-muted-foreground mt-1">{batch.programTitle} • by {batch.mentorName}</p>
        </SheetHeader>

        <div className="space-y-5 mt-5">
          <div className="grid grid-cols-4 gap-3">
            {[
              { l: 'Avg Rating', v: batch.avgRating, icon: Star, c: 'text-warning' },
              { l: 'NPS Score', v: batch.nps, icon: TrendingUp, c: 'text-success' },
              { l: 'Enrolled', v: `${batch.enrolled}/${batch.capacity}`, icon: Users, c: 'text-chart-2' },
              { l: 'Progress', v: `${batch.sessionsCompleted}/${batch.totalSessions}`, icon: Clock, c: 'text-chart-3' },
            ].map(s => (
              <div key={s.l} className="p-3 rounded-lg border border-border">
                <s.icon className={cn('w-4 h-4 mb-1.5', s.c)} />
                <p className="font-bold text-foreground text-base">{s.v}</p>
                <p className="text-xs text-muted-foreground">{s.l}</p>
              </div>
            ))}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary" /> Weekly Ratings Trend
            </h4>
            <div className="border border-border rounded-lg p-3">
              <ResponsiveContainer width="100%" height={140}>
                <LineChart data={batch.weeklyRatings}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 5]} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 6, fontSize: 11 }} />
                  <Line type="monotone" dataKey="rating" stroke="hsl(263,70%,62%)" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-chart-3" /> Participant Feedback ({batch.participants.length})
            </h4>
            <div className="space-y-2">
              {batch.participants.map((p, i) => (
                <div key={i} className="p-3 rounded-lg border border-border">
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <p className="text-sm font-medium text-foreground">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.company}</p>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, idx) => (
                        <Star key={idx} className={cn('w-3.5 h-3.5',
                          idx < p.rating ? 'fill-warning text-warning' : 'text-muted-foreground/30'
                        )} />
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground italic mt-1">"{p.feedback}"</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ────────────────────────────────────────────────────────────────
// IDEAS INBOX — founder-submitted suggestions to mentors
// ────────────────────────────────────────────────────────────────
// ── Status helpers ────────────────────────────────────────────────────────────
const IDEA_STATUS_META = {
  sent:               { label: 'Sent',              cls: 'bg-info/10 border-info/25 text-info',                    dot: 'bg-info'        },
  mentor_reviewing:   { label: 'Mentor Reviewing',  cls: 'bg-warning/10 border-warning/25 text-warning',           dot: 'bg-warning'     },
  partially_accepted: { label: 'Partially Accepted',cls: 'bg-chart-3/10 border-chart-3/25 text-chart-3',           dot: 'bg-chart-3'     },
  all_accepted:       { label: 'Accepted',          cls: 'bg-success/10 border-success/25 text-success',           dot: 'bg-success'     },
  all_rejected:       { label: 'Rejected',          cls: 'bg-destructive/10 border-destructive/25 text-destructive',dot: 'bg-destructive' },
  submitted_to_nexus: { label: 'Under NexusAI Review', cls: 'bg-primary/10 border-primary/25 text-primary',        dot: 'bg-primary'     },
  assigned:           { label: 'Program Assigned',  cls: 'bg-success/10 border-success/25 text-success',           dot: 'bg-success'     },
  discarded:          { label: 'Discarded',         cls: 'bg-muted border-border text-muted-foreground',           dot: 'bg-muted-foreground' },
};

const DECISION_META = {
  pending:  { label: 'Awaiting',  cls: 'text-muted-foreground' },
  accepted: { label: 'Accepted',  cls: 'text-success font-semibold' },
  rejected: { label: 'Rejected',  cls: 'text-destructive font-semibold' },
};

// Pipeline steps for tracking timeline
const PIPELINE_STEPS = [
  { key: 'sent',               label: 'Idea Sent',          icon: Send },
  { key: 'mentor_reviewing',   label: 'Mentor Reviewing',   icon: GraduationCap },
  { key: 'submitted_to_nexus', label: 'NexusAI Review',     icon: Sparkles },
  { key: 'assigned',           label: 'Program Assigned',   icon: CheckCircle },
];

const STATUS_STEP_INDEX = {
  sent: 0,
  mentor_reviewing: 1, partially_accepted: 1, all_accepted: 1, all_rejected: 1,
  submitted_to_nexus: 2,
  assigned: 3, discarded: 3,
};

export const IdeasSection = ({ highlightId, onSuggest }) => {
  const [ideas,   setIdeas]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [openId,  setOpenId]  = useState(null);
  const refs = useRef({});

  const fetchIdeas = async () => {
    setLoading(true); setError(null);
    try {
      const data = await listProgramIdeas();
      setIdeas(Array.isArray(data) ? data : (data?.ideas || []));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchIdeas(); }, []);

  useEffect(() => {
    if (highlightId && refs.current[highlightId]) {
      refs.current[highlightId].scrollIntoView({ behavior: 'smooth', block: 'center' });
      setOpenId(highlightId);
    }
  }, [highlightId, ideas]);

  const statusMeta = (s) => IDEA_STATUS_META[s] || { label: s, cls: 'bg-muted border-border text-muted-foreground', dot: 'bg-muted-foreground' };

  return (
    <div className="space-y-5" data-testid="ideas-section">

      {/* Banner */}
      <div className="p-4 rounded-xl bg-warning/8 border border-warning/20 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Lightbulb className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground">Suggest a Program Idea</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Send an idea to one or more mentors. If they accept, the idea goes to NexusAI for program assignment.
            </p>
          </div>
        </div>
        <Button size="sm" className="gradient-primary text-primary-foreground border-0 gap-1.5 flex-shrink-0" onClick={onSuggest} data-testid="suggest-idea-btn">
          <Plus className="w-3.5 h-3.5" /> Suggest Idea
        </Button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground text-sm">
          <RefreshCw className="w-4 h-4 animate-spin" /> Loading ideas…
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-destructive/8 border border-destructive/20">
          <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0" />
          <p className="text-sm text-destructive flex-1">{error}</p>
          <button onClick={fetchIdeas} className="text-xs underline text-destructive">Retry</button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && ideas.length === 0 && (
        <div className="text-center py-16 border-2 border-dashed border-border rounded-2xl">
          <Inbox className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium text-foreground mb-1">No ideas submitted yet</p>
          <p className="text-xs text-muted-foreground mb-4">Suggest a program idea to one of your mentors to get started.</p>
          <Button size="sm" className="gradient-primary text-primary-foreground border-0 gap-1.5" onClick={onSuggest}>
            <Plus className="w-3.5 h-3.5" /> Suggest Idea
          </Button>
        </div>
      )}

      {/* Idea cards */}
      {!loading && ideas.map(idea => {
        const sm      = statusMeta(idea.status);
        const stepIdx = STATUS_STEP_INDEX[idea.status] ?? 0;
        const isOpen  = openId === String(idea._id);

        return (
          <div key={String(idea._id)}
            ref={el => (refs.current[String(idea._id)] = el)}
            data-testid={`idea-card-${idea._id}`}
            className={cn('rounded-xl border bg-card overflow-hidden', highlightId === String(idea._id) && 'ring-2 ring-primary/40')}>

            {/* Header */}
            <div className="p-4 cursor-pointer" onClick={() => setOpenId(isOpen ? null : String(idea._id))}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-warning/12 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Lightbulb className="w-4.5 h-4.5 text-warning" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-sm text-foreground leading-snug">{idea.title}</p>
                    <span className={cn('text-[11px] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap flex-shrink-0', sm.cls)}>{sm.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{idea.description}</p>

                  {/* Pipeline mini-track */}
                  <div className="flex items-center gap-1 mt-3">
                    {PIPELINE_STEPS.map((step, i) => {
                      const done  = i < stepIdx || (i === stepIdx && idea.status !== 'all_rejected' && idea.status !== 'discarded');
                      const isCur = i === stepIdx;
                      const fail  = isCur && (idea.status === 'all_rejected' || idea.status === 'discarded');
                      return (
                        <div key={step.key} className="flex items-center gap-1">
                          <div className={cn('w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0',
                            fail  ? 'bg-destructive/15' :
                            done  ? 'bg-success/15'     :
                            isCur ? 'bg-primary/15'     : 'bg-muted'
                          )}>
                            <step.icon className={cn('w-3 h-3',
                              fail  ? 'text-destructive' :
                              done  ? 'text-success'     :
                              isCur ? 'text-primary'     : 'text-muted-foreground'
                            )} />
                          </div>
                          <span className={cn('text-[10px] font-medium hidden sm:inline',
                            fail  ? 'text-destructive' :
                            done  ? 'text-success'     :
                            isCur ? 'text-primary'     : 'text-muted-foreground'
                          )}>{step.label}</span>
                          {i < PIPELINE_STEPS.length - 1 && (
                            <div className={cn('flex-1 h-px w-4 mx-0.5', done ? 'bg-success/40' : 'bg-border/60')} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <ChevronRight className={cn('w-4 h-4 text-muted-foreground flex-shrink-0 mt-1 transition-transform', isOpen && 'rotate-90')} />
              </div>

              {/* Assigned program chip — shown directly on card */}
              {idea.status === 'assigned' && (idea.nexusReview?.assignedProgramNames || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2 pl-12">
                  {idea.nexusReview.assignedProgramNames.map((name, i) => (
                    <a key={i} href="/mentors?tab=programs"
                      onClick={e => e.stopPropagation()}
                      className="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-success/12 border border-success/25 text-success hover:bg-success/18 transition-colors">
                      <GraduationCap className="w-3 h-3" />
                      {name}
                      {idea.nexusReview?.assignedMentorNames?.[i] && (
                        <span className="font-normal opacity-75">· {idea.nexusReview.assignedMentorNames[i].split(' ')[0]}</span>
                      )}
                      <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Expanded detail */}
            {isOpen && (
              <div className="border-t border-border px-4 pb-4 pt-3 space-y-4">

                {idea.suggestion && (
                  <div className="text-xs bg-muted/40 rounded-lg p-3">
                    <span className="font-semibold text-foreground">Your suggestion: </span>
                    {idea.suggestion}
                  </div>
                )}

                {/* Mentor responses */}
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Mentor Responses ({idea.mentorResponses?.length || 0})
                  </p>
                  <div className="space-y-2">
                    {(idea.mentorResponses || []).map((r, i) => {
                      const dm = DECISION_META[r.decision] || DECISION_META.pending;
                      return (
                        <div key={i} className="flex items-start gap-3 p-3 rounded-xl border border-border bg-muted/20">
                          <div className="w-7 h-7 rounded-full gradient-primary flex items-center justify-center text-[10px] font-bold text-primary-foreground flex-shrink-0">
                            {r.mentorName.split(' ').slice(0,2).map(n=>n[0]).join('')}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-xs font-semibold text-foreground">{r.mentorName}</p>
                              <span className={cn('text-[11px]', dm.cls)}>{dm.label}</span>
                            </div>
                            {r.responseNote && (
                              <p className="text-[11px] text-muted-foreground mt-1 italic">"{r.responseNote}"</p>
                            )}
                            {r.respondedAt && (
                              <p className="text-[10px] text-muted-foreground mt-0.5">
                                Responded {new Date(r.respondedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </p>
                            )}
                            {r.submittedToNexus && (
                              <p className="text-[11px] text-primary font-medium mt-1 flex items-center gap-1">
                                <Sparkles className="w-3 h-3" /> Submitted to NexusAI
                                {r.submittedAt && ` · ${new Date(r.submittedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* NexusAI review result */}
                {idea.nexusReview && (
                  <div className={cn('p-3 rounded-xl border',
                    idea.nexusReview.decision === 'assigned'  ? 'bg-success/8 border-success/20' :
                    idea.nexusReview.decision === 'discarded' ? 'bg-muted/50 border-border' :
                    'bg-primary/8 border-primary/20'
                  )}>
                    <p className={cn('text-[11px] font-semibold uppercase tracking-wide mb-2 flex items-center gap-1.5',
                      idea.nexusReview.decision === 'assigned' ? 'text-success' :
                      idea.nexusReview.decision === 'discarded' ? 'text-muted-foreground' : 'text-primary'
                    )}>
                      <Sparkles className="w-3 h-3" />
                      NexusAI — {idea.nexusReview.decision === 'assigned' ? 'Program Assigned ✓' : 'Discarded'}
                    </p>

                    {/* Assigned programs list */}
                    {idea.nexusReview.decision === 'assigned' && (idea.nexusReview.assignedProgramNames || []).length > 0 && (
                      <div className="space-y-1.5 mb-2">
                        {idea.nexusReview.assignedProgramNames.map((name, i) => (
                          <a
                            key={i}
                            href={`/mentors?tab=programs`}
                            className="flex items-center gap-2 p-2 rounded-lg bg-success/10 border border-success/20 hover:bg-success/15 transition-colors group"
                          >
                            <GraduationCap className="w-3.5 h-3.5 text-success flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-success leading-snug">{name}</p>
                              {idea.nexusReview.assignedMentorNames?.[i] && (
                                <p className="text-[11px] text-muted-foreground">
                                  Under {idea.nexusReview.assignedMentorNames[i]}
                                </p>
                              )}
                            </div>
                            <ExternalLink className="w-3 h-3 text-success opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    )}

                    {idea.nexusReview.notes && (
                      <p className="text-xs text-foreground mb-1">{idea.nexusReview.notes}</p>
                    )}
                    {idea.nexusReview.discardReason && (
                      <p className="text-xs text-muted-foreground italic">{idea.nexusReview.discardReason}</p>
                    )}
                    {idea.nexusReview.decidedBy && (
                      <p className="text-[10px] text-muted-foreground mt-1">By {idea.nexusReview.decidedBy}
                        {idea.nexusReview.decidedAt && ` · ${new Date(idea.nexusReview.decidedAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}`}
                      </p>
                    )}
                  </div>
                )}

                <p className="text-[10px] text-muted-foreground">
                  Submitted {new Date(idea.createdAt).toLocaleDateString('en-IN', { day:'numeric', month:'long', year:'numeric' })}
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
// SUGGEST IDEA DIALOG — multi-mentor checkboxes + real API
// ────────────────────────────────────────────────────────────────
const BLANK_FORM = { title: '', description: '', suggestion: '' };

export const SuggestIdeaDialog = ({ open, onClose, mentors }) => {
  const [form,        setForm]        = useState(BLANK_FORM);
  const [selected,    setSelected]    = useState(new Set());
  const [submitting,  setSubmitting]  = useState(false);
  const [done,        setDone]        = useState(false);

  const activeMentors = (mentors || []).filter(m =>
    ['Active', 'Integration Setup Required', 'Integration Requested', 'active', 'mentor_active'].includes(m.status)
  );

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const reset = () => {
    setForm(BLANK_FORM);
    setSelected(new Set());
    setSubmitting(false);
    setDone(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Please add a title and description');
      return;
    }
    if (selected.size === 0) {
      toast.error('Select at least one mentor');
      return;
    }
    setSubmitting(true);
    try {
      await createProgramIdea({
        title:            form.title.trim(),
        description:      form.description.trim(),
        suggestion:       form.suggestion.trim(),
        mentorProfileIds: [...selected],
      });
      setDone(true);
      toast.success('Idea sent!', { description: `Sent to ${selected.size} mentor${selected.size > 1 ? 's' : ''}. You'll be notified when they respond.` });
    } catch (e) {
      toast.error(e.message || 'Failed to send idea');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="suggest-idea-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-warning/15 flex items-center justify-center flex-shrink-0">
              <Lightbulb className="w-5 h-5 text-warning" />
            </div>
            <div>
              <DialogTitle className="text-base">Suggest a Program Idea</DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5">If a mentor accepts, the idea goes to NexusAI for program assignment.</p>
            </div>
          </div>
        </DialogHeader>

        {done ? (
          /* ── Success state ── */
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="w-14 h-14 rounded-2xl bg-success/15 flex items-center justify-center">
              <CheckCircle className="w-7 h-7 text-success" />
            </div>
            <div>
              <p className="font-semibold text-foreground">Idea sent successfully!</p>
              <p className="text-xs text-muted-foreground mt-1">
                Your idea has been sent to {selected.size} mentor{selected.size > 1 ? 's' : ''}.<br />
                You'll be notified when they respond.
              </p>
            </div>
            <Button className="gradient-primary text-primary-foreground border-0" onClick={handleClose}>
              Close
            </Button>
          </div>
        ) : (
          /* ── Form ── */
          <div className="space-y-4 mt-2">
            {/* Title */}
            <div>
              <Label className="text-sm">Title <span className="text-destructive">*</span></Label>
              <Input
                className="mt-1.5"
                placeholder="e.g. Founder Storytelling Workshop"
                value={form.title}
                onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                data-testid="idea-title-input"
                maxLength={80}
                disabled={submitting}
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-sm">Description <span className="text-destructive">*</span></Label>
              <Textarea
                className="mt-1.5"
                rows={3}
                placeholder="What should the program cover? Who is it for?"
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                data-testid="idea-description-input"
                maxLength={400}
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground mt-1 text-right">{form.description.length}/400</p>
            </div>

            {/* Suggestion */}
            <div>
              <Label className="text-sm">Suggestion <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea
                className="mt-1.5"
                rows={2}
                placeholder="Any specific angle, case study, or format you'd like?"
                value={form.suggestion}
                onChange={e => setForm(p => ({ ...p, suggestion: e.target.value }))}
                data-testid="idea-suggestion-input"
                maxLength={200}
                disabled={submitting}
              />
            </div>

            {/* Multi-mentor selector */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm">Send to Mentors <span className="text-destructive">*</span></Label>
                {selected.size > 0 && (
                  <span className="text-xs text-primary font-semibold">{selected.size} selected</span>
                )}
              </div>

              {activeMentors.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No active mentors available.</p>
              ) : (
                <div className="rounded-xl border border-border divide-y divide-border max-h-48 overflow-y-auto">
                  {activeMentors.map(m => {
                    const id  = String(m._id);
                    const sel = selected.has(id);
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => toggle(id)}
                        disabled={submitting}
                        data-testid={`idea-mentor-${id}`}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors',
                          sel ? 'bg-primary/6' : 'hover:bg-muted/40'
                        )}
                      >
                        {/* Checkbox indicator */}
                        <div className={cn(
                          'w-4 h-4 rounded flex items-center justify-center border flex-shrink-0 transition-colors',
                          sel ? 'bg-primary border-primary' : 'border-border bg-background'
                        )}>
                          {sel && <CheckCircle className="w-3 h-3 text-primary-foreground" />}
                        </div>

                        {/* Avatar */}
                        <div className="w-7 h-7 rounded-full gradient-primary flex items-center justify-center text-[10px] font-bold text-primary-foreground flex-shrink-0">
                          {m.name.split(' ').slice(0, 2).map(n => n[0]).join('')}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className={cn('text-xs font-semibold', sel ? 'text-primary' : 'text-foreground')}>{m.name}</p>
                          {m.expertise?.length > 0 && (
                            <p className="text-[11px] text-muted-foreground truncate">{m.expertise.slice(0, 3).join(' · ')}</p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" className="border-border" onClick={handleClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                className="gradient-primary text-primary-foreground border-0 gap-1.5"
                onClick={handleSubmit}
                disabled={submitting || selected.size === 0 || !form.title.trim() || !form.description.trim()}
                data-testid="idea-submit-btn"
              >
                {submitting ? (
                  <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="w-3.5 h-3.5" /> Send to {selected.size > 0 ? `${selected.size} Mentor${selected.size > 1 ? 's' : ''}` : 'Mentor'}</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
